import { EyeOff, AlertTriangle, XCircle, Copy, ExternalLink, RotateCcw, Info } from 'lucide-react';
import { formatOfferText } from '../../../utils/textUtils';

export default function IgnoredOffersTab({ ignoredOffers, validationResults, restoreOffer, copyToClipboard }) {
    return (
        <div className="p-8 space-y-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-6 border border-slate-200/60">
                <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                        <EyeOff size={24} className="text-slate-700" />
                    </div>
                    Ignored Offers
                </h3>
                <p className="text-slate-600 mt-2 font-medium">Offers hidden from validation results per page - Click restore to un-hide</p>
            </div>

            {/* Ignored offers by page */}
            {Object.entries(ignoredOffers).map(([pageUrl, pageIgnored]) => {
                const hasMismatches = pageIgnored?.mismatches?.length > 0;
                const hasMissing = pageIgnored?.missing?.length > 0;

                if (!hasMismatches && !hasMissing) return null;

                const pageInfo = validationResults?.validations[pageUrl]?.pageInfo;

                return (
                    <div key={pageUrl} className="bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm">
                        {/* Page Header */}
                        <div className="mb-6 pb-4 border-b border-slate-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-bold text-lg text-slate-900 mb-1">
                                        {pageInfo?.type || 'Page'}
                                    </h4>
                                    <div className="text-sm text-slate-600 font-mono bg-slate-100 px-3 py-1.5 rounded-lg break-all">
                                        {pageUrl}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => copyToClipboard(pageUrl)}
                                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                                        title="Copy URL"
                                    >
                                        <Copy size={16} className="text-slate-600" />
                                    </button>
                                    <a
                                        href={pageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                                        title="Open Page"
                                    >
                                        <ExternalLink size={16} className="text-slate-600" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Ignored Partial Matches */}
                        {hasMismatches && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle size={16} className="text-amber-600" />
                                    <h5 className="font-bold text-amber-800">
                                        Ignored Partial Matches ({pageIgnored.mismatches.length})
                                    </h5>
                                </div>
                                <div className="space-y-2">
                                    {pageIgnored.mismatches.map((offer, i) => (
                                        <div key={i} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3 hover:bg-amber-100 transition-colors duration-200">
                                            <span className="text-sm text-amber-900 font-medium flex-1">{formatOfferText(offer)}</span>
                                            <button
                                                onClick={() => restoreOffer(pageUrl, offer, 'mismatches')}
                                                className="p-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors duration-200 border border-amber-300 flex-shrink-0"
                                                title="Restore this offer"
                                            >
                                                <RotateCcw size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Ignored Missing Offers */}
                        {hasMissing && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <XCircle size={16} className="text-red-600" />
                                    <h5 className="font-bold text-red-800">
                                        Ignored Missing Offers ({pageIgnored.missing.length})
                                    </h5>
                                </div>
                                <div className="space-y-2">
                                    {pageIgnored.missing.map((offer, i) => (
                                        <div key={i} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3 hover:bg-red-100 transition-colors duration-200">
                                            <span className="text-sm text-red-900 font-medium flex-1">{formatOfferText(offer)}</span>
                                            <button
                                                onClick={() => restoreOffer(pageUrl, offer, 'missing')}
                                                className="p-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg transition-colors duration-200 border border-red-300 flex-shrink-0"
                                                title="Restore this offer"
                                            >
                                                <RotateCcw size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Help Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                    <Info size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h4 className="font-bold text-blue-900 mb-2">About Ignored Offers</h4>
                        <p className="text-sm text-blue-700 leading-relaxed">
                            Ignored offers are stored per page and will not appear in validation results.
                            This is useful for niche offers that don't need to be checked. Click the restore button
                            (circular arrow) to bring an offer back to the validation results.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
